import {cloneDeep} from 'lodash';
import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {environment} from '../../../environments/environment';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {StorageService} from '../../services/storage/storage.service';
import {GuidService} from '../../services/guid/guid.service';
import {BillingInfoDTO, InvoiceDTO, PaymentMethodResponseDTO, PlanDTO, SubscriptionDTO, SubscriptionResponseDTO} from '../../dtos/billing';
import {ILimitsSubscription} from './limits-subscription.interface';
import {IPaymentMethod} from './payment-method.interface';
import {firstValueFrom} from 'rxjs';
import {ClinicActions} from '../clinic';
import {AnyObject} from '../../interfaces/utils';

export interface IRPlan {
  plans: PlanDTO[];
  subscriptions: SubscriptionDTO[];
  subscriptionActive?: SubscriptionDTO;
  subscriptionActiveWithInvoices?: AnyObject;
  invoices?: InvoiceDTO[];
  subscriptionStripe?: SubscriptionResponseDTO;
  indicatorsSubscription?: ILimitsSubscription;
  paymentMethod?: IPaymentMethod;
  billingInfo?: BillingInfoDTO;
  urlChangePaymentMethod?: PaymentMethodResponseDTO;
}

export namespace PlanActions {
  export class Get {
    static readonly type: string = '[Plan] Get Plans Action';
    constructor() { }
  }

  export class SessionSubscriptionToPlan {
    static readonly type: string = '[Plan] Session Subscription To Plan Action';
    constructor(public clinicId: string, public planId: string) { }
  }

  export class SetSubscriptions {
    static readonly type: string = '[Plan] Set Subscriptions Action';
    constructor(public subscription: SubscriptionDTO) { }
  }

  export class GetSubscriptionActive {
    static readonly type: string = '[Plan] Get Subscription Active Action';
    constructor(public clinicId: string) { }
  }

  export class GetIndicators {
    static readonly type: string = '[Plan] Get Indicators Subscription Action';
    constructor(public clinicId: string) { }
  }

  export class GetBillingInfo {
    static readonly type: string = '[Plan] Get Billing Info Action';
    constructor(public clinicId: string) { }
  }

  export class ChangePaymentMethod {
    static readonly type: string = '[Plan] Change Payment Method Action';
    constructor(public clinicId: string) { }
  }

  export class Cancel {
    static readonly type: string = '[Plan] Cancel Subscription Action';
    constructor(public clinicId: string) { }
  }

  export class AddFreeDays {
    static readonly type: string = '[Plan] Add Free Days To Plan Action';
    constructor(public clinicId: string) { }
  }

  export class Update {
    static readonly type: string = '[Plan] Update Plan Action';
    constructor(public clinicId: string, public planId: string) { }
  }

  export class CancelNextPlan {
    static readonly type: string = '[Plan] Cancel Next Plan Action';
    constructor(public clinicId: string) { }
  }
}

@State<IRPlan>({
  name: 'PlanState',
  defaults: {
    plans: [],
    subscriptions: [],
  },
})

@Injectable()
export class PlanState {
  constructor(
    private baseService: BaseService<any>,
    private storageService: StorageService,
    private guidService: GuidService,
    private store: Store
  ) { }

  url: string = environment.SERVER_URL;


  @Selector()
  public static getPlans(state: IRPlan): PlanDTO[] {
    return state.plans;
  }


  @Selector()
  public static getSubscriptionStripe(state: IRPlan): any {
    return state.subscriptionStripe;
  }

  @Selector()
  public static getSubscriptionActive(state: IRPlan): SubscriptionDTO {
    return state.subscriptionActive;
  }

  @Selector()
  public static getIndicatorsSubscription(state: IRPlan): ILimitsSubscription {
    return state.indicatorsSubscription;
  }

  @Selector()
  public static getBillingInfo(state: IRPlan): BillingInfoDTO {
    return state.billingInfo;
  }

  @Selector()
  public static getPaymentMethodResponse(state: IRPlan): PaymentMethodResponseDTO {
    return state.urlChangePaymentMethod;
  }

  @Action(PlanActions.Get)
  async getPlansAction(ctx: StateContext<IRPlan>): Promise<void> {
    const response: DataBaseServiceResponse<PlanDTO[]> = await firstValueFrom((this.baseService.get(`${this.url}/billing/plans`, void 0)));
    if (response.error) throw new Error(response.message);
    // const planes: PlanDTO[] = response.entity.filter(item => item.amount !== 20000 && item.amount !== 49000);
    ctx.patchState({
      plans: response.entity
    });
  }

  @Action(PlanActions.SessionSubscriptionToPlan)
  async sessionSubscriptionToPlanAction(ctx: StateContext<IRPlan>, action?: PlanActions.SessionSubscriptionToPlan): Promise<void> {
    if (!this.storageService.getLocalItem('idempotencyKey')) {
      this.storageService.setLocalItem('idempotencyKey', this.guidService.guid());
    }
    const idempotencyKey = this.storageService.getLocalItem('idempotencyKey');
    const response: DataBaseServiceResponse<SubscriptionResponseDTO> = await firstValueFrom(this.baseService.post(`${this.url}/billing/session/subscriptions/${action.clinicId}`,
      { planId: action.planId },
      void 0, void 0, {
      headers: {
        idempotencyKey
      }
    }));
    if (response.error) {
      this.storageService.removeLocalItem('idempotencyKey');
      throw new Error(response.message);
    }
    ctx.patchState({
      subscriptionStripe: response.entity
    });
    this.storageService.removeLocalItem('idempotencyKey');
  }

  @Action(PlanActions.SetSubscriptions)
  async setSubscriptionAction(ctx: StateContext<IRPlan>, action?: PlanActions.SetSubscriptions): Promise<void> {
    ctx.patchState({
      subscriptionActive: action.subscription
    });
  }

  @Action(PlanActions.GetSubscriptionActive)
  async getSubscriptionActiveAction(ctx: StateContext<IRPlan>, action?: PlanActions.GetSubscriptionActive): Promise<void> {
    const response: DataBaseServiceResponse<SubscriptionDTO> = await firstValueFrom(this.baseService.get(`${this.url}/billing/subscriptions/${action.clinicId}/active`, void 0,
      void 0, void 0));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      subscriptionActive: response.entity
    });
  }

  @Action(PlanActions.GetIndicators)
  async getIndicatorsSubscriptionAction(ctx: StateContext<IRPlan>, action?: PlanActions.GetIndicators): Promise<void> {
    const response: DataBaseServiceResponse<ILimitsSubscription> = await firstValueFrom(this.baseService.get(`${this.url}/billing/subscriptions/${action.clinicId}/limits`, void 0,
      void 0, void 0));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      indicatorsSubscription: response.entity
    });
  }

  @Action(PlanActions.GetBillingInfo)
  async getBillingInfoAction(ctx: StateContext<IRPlan>, action?: PlanActions.GetBillingInfo): Promise<void> {
    const response: DataBaseServiceResponse<BillingInfoDTO> = await firstValueFrom(this.baseService.get(`${this.url}/billing/subscriptions/${action.clinicId}/info`, void 0,
      void 0, void 0));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      billingInfo: response.entity,
      invoices: response.entity.invoices,
      indicatorsSubscription: response.entity.limits,
      paymentMethod: response.entity.paymentMethod
    });
  }

  @Action(PlanActions.ChangePaymentMethod)
  async changePaymentMethodAction(ctx: StateContext<IRPlan>, action?: PlanActions.ChangePaymentMethod): Promise<void> {
    const response: DataBaseServiceResponse<PaymentMethodResponseDTO> = await firstValueFrom(this.baseService.post(`${this.url}/billing/session/payment-methods/${action.clinicId}`, void 0, void 0,
      void 0, void 0));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      urlChangePaymentMethod: response.entity
    });
  }

  @Action(PlanActions.AddFreeDays)
  async addFreeDaysToPlanAction(ctx: StateContext<IRPlan>, action?: PlanActions.AddFreeDays): Promise<void> {
    if (!this.storageService.getLocalItem('idempotencyKeyPromotion')) {
      this.storageService.setLocalItem('idempotencyKeyPromotion', this.guidService.guid());
    }
    const idempotencyKey = this.storageService.getLocalItem('idempotencyKeyPromotion');
    const response: DataBaseServiceResponse<BillingInfoDTO> = await firstValueFrom(this.baseService.patch(
      `${this.url}/billing/subscriptions/${action.clinicId}/promotion`,
      {}, void 0,
      void 0, {
      headers: {
        idempotencyKey
      }
    }));
    if (response.error) throw new Error(response.message);
    const info: BillingInfoDTO = cloneDeep(ctx.getState().billingInfo);
    info.nextPayment = response.entity.nextPayment;
    ctx.patchState({
      billingInfo: info,
    });
    this.storageService.removeLocalItem('idempotencyKeyPromotion');
    await firstValueFrom((this.store.dispatch(new ClinicActions.UpdateCancellationPromo(true))));
  }

  @Action(PlanActions.Update)
  async updatePlanAction(ctx: StateContext<IRPlan>, action?: PlanActions.Update): Promise<void> {
    if (!this.storageService.getLocalItem('idempotencyKeyUpdateSubscription')) {
      this.storageService.setLocalItem('idempotencyKeyUpdateSubscription', this.guidService.guid());
    }
    const idempotencyKey = this.storageService.getLocalItem('idempotencyKeyUpdateSubscription');
    const response: DataBaseServiceResponse<BillingInfoDTO> = await firstValueFrom(this.baseService.post(
      `${this.url}/billing/subscriptions/${action.clinicId}/upgrade`,
      { planId: action.planId },
      void 0, void 0,
      {
        headers: {
          idempotencyKey
        }
      }));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      billingInfo: response.entity,
      subscriptionActive: response.entity.subscription,
    });
    this.storageService.removeLocalItem('idempotencyKeyUpdateSubscription');
  }

  @Action(PlanActions.Cancel)
  async cancelSubscriptionAction(ctx: StateContext<IRPlan>, action?: PlanActions.Cancel): Promise<void> {
    if (!this.storageService.getLocalItem('idempotencyKeyCancelSubscription')) {
      this.storageService.setLocalItem('idempotencyKeyCancelSubscription', this.guidService.guid());
    }
    const idempotencyKey = this.storageService.getLocalItem('idempotencyKeyCancelSubscription');
    const response: DataBaseServiceResponse<BillingInfoDTO> = await firstValueFrom(
      this.baseService.post(`${this.url}/billing/subscription/${action.clinicId}/cancel`, void 0, void 0, void 0,
      {
        headers: {
          idempotencyKey
        }
      }, void 0));
    if (response.error) throw new Error(response.message);
    const info: BillingInfoDTO = cloneDeep(ctx.getState().billingInfo);
    // estaba esa línea de abajo, pero no debería recibir el objeto entity completo
    // info.subscription = response.entity;
    info.subscription = response.entity.subscription;
    ctx.patchState({
      billingInfo: response.entity,
    });
    this.storageService.removeLocalItem('idempotencyKeyCancelSubscription');
  }

  @Action(PlanActions.CancelNextPlan)
  async cancelNextPlanAction(ctx: StateContext<IRPlan>, action?: PlanActions.CancelNextPlan): Promise<void> {
    if (!this.storageService.getLocalItem('idempotencyKeyCancelNextPlan')) {
      this.storageService.setLocalItem('idempotencyKeyCancelNextPlan', this.guidService.guid());
    }
    const idempotencyKey = this.storageService.getLocalItem('idempotencyKeyCancelNextPlan');
    const response: DataBaseServiceResponse<BillingInfoDTO> = await firstValueFrom(this.baseService.post(`${this.url}/billing/subscription/${action.clinicId}/next-plan/cancel`, void 0, void 0, void 0,
      {
        headers: {
          idempotencyKey
        }
      }, void 0));
    if (response.error) throw new Error(response.message);
    const info: BillingInfoDTO = cloneDeep(ctx.getState().billingInfo);
    // estaba esa línea de abajo, pero no debería recibir el objeto entity completo
    // info.subscription = response.entity;
    info.subscription = response.entity.subscription;
    ctx.patchState({
      billingInfo: response.entity,
    });
    this.storageService.removeLocalItem('idempotencyKeyCancelNextPlan');
  }
}
